import { useBrandingTheme } from '@/hooks/stores/useBrandingTheme';
import { BankingInfoResponse } from '@/types/bankingInfo';
import { generateColorShades } from '@/utils/color';
import { Box, Card, Stack, Typography, useMediaQuery } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { abbreviateAmount, formatCurrency } from '@/utils/currency';
import FDICIcon from '@/components/icons/FDICIcon';

const size = {
  width: 250,
  height: 250,
};

type AdvisorDashboardAvailableBalanceProps = {
  bankingInfo: BankingInfoResponse;
};

export default function AdvisorDashboardAvailableBalance({
  bankingInfo,
}: AdvisorDashboardAvailableBalanceProps) {
  const isLargeScreen = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const { brandingTheme } = useBrandingTheme();

  const { totalAvailableBalance: companyTotalBalance } =
    bankingInfo.companyInfo;
  const { totalAvailableBalance: advisorTotalBalance } = bankingInfo;

  const lightPrimaryColor = generateColorShades(
    brandingTheme.primaryColor,
    300,
  );
  const data = [
    {
      value: Number(advisorTotalBalance.value),
      color: brandingTheme.primaryColor,
    },
    {
      value:
        Number(companyTotalBalance.value) - Number(advisorTotalBalance.value),
      color: lightPrimaryColor,
    },
  ];

  return (
    <Card>
      <Typography variant="h5" mb={1}>
        {bankingInfo.companyInfo.name}
      </Typography>
      <Typography mb={1}>Total Available Balance</Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          alignItems: 'center',
          gap: 2,
        }}>
        <Box sx={{ position: 'relative' }} flex={0}>
          <PieChart series={[{ data, innerRadius: 80 }]} {...size} />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
            }}>
            <Typography
              sx={{
                fontWeight: 'bold',
                fontSize: '35px',
                color: 'gray',
              }}>
              ${abbreviateAmount(Number(companyTotalBalance.value))}
            </Typography>
            <FDICIcon />
          </Box>
        </Box>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: isLargeScreen ? 'row' : 'column',
              alignItems: isLargeScreen ? 'center' : 'flex-start',
            }}>
            <Box
              sx={{
                backgroundColor: brandingTheme.primaryColor,
                height: 16,
                width: 16,
              }}
            />
            <Typography flex={1} variant="body2">
              <Typography variant="body2" fontWeight="bold" component="span">
                {formatCurrency({ amount: advisorTotalBalance.value })}
              </Typography>
              : Available balance for bank accounts for which you support.
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: isLargeScreen ? 'row' : 'column',
              alignItems: isLargeScreen ? 'center' : 'flex-start',
            }}>
            <Box
              sx={{
                backgroundColor: lightPrimaryColor,
                height: 16,
                width: 16,
              }}
            />
            <Typography flex={1} variant="body2">
              <Typography variant="body2" fontWeight="bold" component="span">
                {formatCurrency({ amount: companyTotalBalance.value })}
              </Typography>
              : Available balance across{' '}
              <Typography fontWeight="bold" component="span" variant="body2">
                {bankingInfo.companyInfo.name}
              </Typography>
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: isLargeScreen ? 'row' : 'column',
              alignItems: isLargeScreen ? 'center' : 'flex-start',
            }}>
            <FDICIcon />
            <Typography variant="body2">
              Available Balance is fully FDIC insured.
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Card>
  );
}
